import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Alert from "@mui/material/Alert";
import { green, grey } from "@mui/material/colors";
import { LightGreyBox } from "../../styled";
import { IconStackButtonFullHeight } from "../../styled/button";
import { parseFloatToFixed } from "../../../extras";
import { alpha } from "@mui/material/styles";
import { getDeliveryCenterConfig } from "../../../api/DeliveryCenter/deliveryCenter";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";

const HOVER_DURATION = 1600;
const BLINK_DURATION = 300;
const ALERT_DURATION = 2000;

export default function PackedConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  orderProducts = [],
  confirmText = "Confirmar",
  cancelText = "Cancelar",
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [checkedItems, setCheckedItems] = useState([]);
  const [isHolding, setIsHolding] = useState(false);
  const [showRedBlink, setShowRedBlink] = useState(false);
  const [showClickAlert, setShowClickAlert] = useState(false);
  const [clickAndHoldEnabled, setClickAndHoldEnabled] = useState(false);

  const holdTimerRef = useRef(null);
  const blinkTimeoutRef = useRef(null);
  const alertTimeoutRef = useRef(null);

  const loadConfig = async () => {
    try {
      const params = {
        config: "click_and_hold_to_confirm_packed_item",
      };
      const { data } = await getDeliveryCenterConfig(params);

      if (!data) {
        return;
      }

      const configValue = data.click_and_hold_to_confirm_packed_item;

      setClickAndHoldEnabled(configValue ?? false);
    } catch (err) {
      console.error("Error loading config:", err);
      dispatchSnackBarMessage(
        "config-load-error",
        "Erro ao carregar configurações de controle de embalagem",
        "error"
      );
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const resetHoldState = () => {
    setIsHolding(false);

    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const handleItemComplete = () => {
    const currentItem = orderProducts[currentIndex];

    resetHoldState();
    triggerRedBlink();
    setCheckedItems([...checkedItems, currentItem]);

    const isLastItem = currentIndex >= orderProducts?.length - 1;

    if (isLastItem) {
      setCurrentIndex(orderProducts?.length || 0);
      return;
    }

    setCurrentIndex(currentIndex + 1);
  };

  const startHoldTimer = () => {
    setIsHolding(true);

    holdTimerRef.current = setTimeout(() => {
      handleItemComplete();
    }, HOVER_DURATION);
  };

  const showClickAlertMessage = () => {
    setShowClickAlert(true);

    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }

    alertTimeoutRef.current = setTimeout(() => {
      setShowClickAlert(false);
    }, ALERT_DURATION);
  };

  const handleMouseDown = (event) => {
    event.preventDefault();

    if (!clickAndHoldEnabled) {
      return;
    }

    startHoldTimer();
  };

  const handleMouseUp = () => {
    if (!clickAndHoldEnabled) {
      return;
    }

    const wasHolding = isHolding;
    const hadTimer = holdTimerRef.current !== null;

    resetHoldState();

    const userReleasedEarly = wasHolding && hadTimer;

    if (userReleasedEarly) {
      showClickAlertMessage();
    }
  };

  const handleMouseLeave = () => {
    if (!clickAndHoldEnabled) {
      return;
    }

    resetHoldState();
  };

  const handleTouchStart = (event) => {
    event.preventDefault();

    if (!clickAndHoldEnabled) {
      return;
    }

    startHoldTimer();
  };

  const handleTouchEnd = () => {
    if (!clickAndHoldEnabled) {
      return;
    }

    const wasHolding = isHolding;
    const hadTimer = holdTimerRef.current !== null;

    resetHoldState();

    const userReleasedEarly = wasHolding && hadTimer;

    if (userReleasedEarly) {
      showClickAlertMessage();
    }
  };

  const handleSimpleClick = () => {
    if (clickAndHoldEnabled) {
      return;
    }

    handleItemComplete();
  };

  const triggerRedBlink = () => {
    setShowRedBlink(true);

    if (blinkTimeoutRef.current) {
      clearTimeout(blinkTimeoutRef.current);
    }

    blinkTimeoutRef.current = setTimeout(() => {
      setShowRedBlink(false);
    }, BLINK_DURATION);
  };

  const handleFinalConfirm = () => {
    onConfirm();
    handleDialogClose();
  };

  const handleDialogClose = () => {
    onClose();
    resetComponentState();
  };

  const resetComponentState = () => {
    setCheckedItems([]);
    setCurrentIndex(0);
    setShowClickAlert(false);
    resetHoldState();
    setShowRedBlink(false);
  };

  const cleanupTimers = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
    }
    if (blinkTimeoutRef.current) {
      clearTimeout(blinkTimeoutRef.current);
    }
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }
  };

  useEffect(() => {
    if (open) {
      resetComponentState();
    }

    return cleanupTimers;
  }, [open]);

  useEffect(() => {
    return cleanupTimers;
  }, []);

  const currentProduct = orderProducts[currentIndex];
  const showButtons = currentIndex >= (orderProducts?.length || 0);
  const productQuantity = parseFloatToFixed(currentProduct?.quantity || 0, 0);
  const productTitle = currentProduct?.product_title || "";
  const itemCountText = `Item ${currentIndex + 1} de ${orderProducts?.length}`;

  const renderCurrentItem = () => {
    const itemBoxProps = {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onClick: handleSimpleClick,
      sx: {
        position: "relative",
        cursor: "pointer",
        userSelect: "none",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
      },
    };

    const shouldShowProgressBar = clickAndHoldEnabled && isHolding;
    const shouldShowPulseIndicator = clickAndHoldEnabled && isHolding;

    const progressBarStyles = {
      position: "absolute",
      bottom: 0,
      left: 0,
      height: "100%",
      backgroundColor: green["500"],
      opacity: 0.3,
      animation: `fillProgress ${HOVER_DURATION}ms linear forwards`,
      transformOrigin: "bottom",
      zIndex: 1,
    };

    const pulseIndicatorStyles = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 80,
      height: 80,
      borderRadius: "50%",
      backgroundColor: green["500"],
      opacity: 0.4,
      animation: "pulse 0.8s ease-in-out infinite",
      zIndex: 2,
      pointerEvents: "none",
    };

    const itemContent = (
      <LightGreyBox>
        <style>
          {`
            @keyframes fillProgress {
              from {
                width: 0%;
              }
              to {
                width: 100%;
              }
            }
            
            @keyframes pulse {
              0% {
                transform: translate(-50%, -50%) scale(0.8);
                opacity: 0.6;
              }
              50% {
                transform: translate(-50%, -50%) scale(1.1);
                opacity: 0.3;
              }
              100% {
                transform: translate(-50%, -50%) scale(0.8);
                opacity: 0.6;
              }
            }
          `}
        </style>
        <List>
          <ListItem {...itemBoxProps}>
            <Box
              sx={{ position: "relative", width: "100%", overflow: "hidden" }}
            >
              {shouldShowProgressBar && <Box sx={progressBarStyles} />}

              <IconStackButtonFullHeight
                padding={80}
                bgColor={grey["200"]}
                bgColorHover={isHolding ? grey["300"] : grey["300"]}
                sx={{ position: "relative", zIndex: 2 }}
              >
                <Card elevation={0} sx={{ backgroundColor: "transparent" }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: grey["600"] }}
                  >
                    {productQuantity}x {productTitle}
                  </Typography>
                </Card>
              </IconStackButtonFullHeight>

              {shouldShowPulseIndicator && <Box sx={pulseIndicatorStyles} />}
            </Box>
          </ListItem>
        </List>
        <DialogContentText sx={{ textAlign: "center", mt: 1 }}>
          {itemCountText}
        </DialogContentText>
        {clickAndHoldEnabled && (
          <DialogContentText
            sx={{
              textAlign: "center",
              fontSize: "0.875rem",
              color: grey["600"],
            }}
          >
            Clique e mantenha pressionado por 1.6 segundos
          </DialogContentText>
        )}
        {!clickAndHoldEnabled && (
          <DialogContentText
            sx={{
              textAlign: "center",
              fontSize: "0.875rem",
              color: grey["600"],
            }}
          >
            Clique para confirmar
          </DialogContentText>
        )}
      </LightGreyBox>
    );

    return itemContent;
  };

  const renderFinalConfirmation = () => {
    const confirmationContent = (
      <>
        <DialogContentText>{description}</DialogContentText>
        <List>
          {checkedItems.map((item, index) => {
            const itemQuantity = parseFloatToFixed(item.quantity || 0, 0);
            const itemTitle = item.product_title || "";
            const listItemKey = `${index}-${item.product_title}`;

            return (
              <ListItem key={listItemKey}>
                <ListItemText primary={`${itemQuantity}x ${itemTitle}`} />
              </ListItem>
            );
          })}
        </List>
      </>
    );

    return confirmationContent;
  };

  const renderDialogActions = () => {
    if (!showButtons) {
      const hiddenElement = <div style={{ display: "none" }} />;
      return hiddenElement;
    }

    const actionButtons = (
      <>
        <Button onClick={handleDialogClose} color="secondary">
          {cancelText}
        </Button>
        <Button onClick={handleFinalConfirm} color="primary">
          {confirmText}
        </Button>
      </>
    );

    return actionButtons;
  };

  const renderClickAlert = () => {
    const alertProps = {
      severity: "info",
      sx: {
        position: "fixed",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10000,
        minWidth: 200,
      },
    };

    const alertContent = (
      <Fade in={showClickAlert} timeout={300}>
        <Alert {...alertProps}>Clicar e segurar</Alert>
      </Fade>
    );

    return alertContent;
  };

  const dialogProps = {
    open: open,
    onClose: handleDialogClose,
    fullWidth: true,
  };

  const redBlinkProps = {
    in: showRedBlink,
    timeout: BLINK_DURATION,
  };

  const blinkOverlayProps = {
    sx: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: alpha(green["500"], 0.2),
      opacity: 0.3,
      zIndex: 9999,
      pointerEvents: "none",
    },
  };

  return (
    <>
      <Dialog {...dialogProps}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {currentIndex < orderProducts?.length
            ? renderCurrentItem()
            : renderFinalConfirmation()}
        </DialogContent>
        <DialogActions>{renderDialogActions()}</DialogActions>
      </Dialog>

      <Fade {...redBlinkProps}>
        <Box {...blinkOverlayProps} />
      </Fade>

      {renderClickAlert()}
    </>
  );
}
